import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { CurrencyInput, formatCurrencyValue, roundMoney } from '@/components/ui/currency-input';
import { format, differenceInDays } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  Store, Calendar, Banknote, Plus, CheckCircle2, AlertTriangle, 
  XCircle, RefreshCw, Clock, FileText, ChevronRight, Ban,
  Wallet, TrendingUp, ArrowRight, AlertCircle
} from 'lucide-react';
import { StorePeriodFilter, useStorePeriodFilter } from '@/components/retail/StorePeriodFilter';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Format currency - use the safe version
const formatCurrency = formatCurrencyValue;

// Status badge
const DepositStatusBadge = ({ status }) => {
  const config = {
    DRAFT: { label: 'Rascunho', color: 'bg-slate-100 text-slate-600', icon: FileText },
    CONFIRMED: { label: 'Confirmado', color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2 },
    CANCELLED: { label: 'Cancelado', color: 'bg-red-50 text-red-700', icon: Ban },
  };
  
  const cfg = config[status] || config.DRAFT;
  const Icon = cfg.icon;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{cfg.label}</span>
    </div>
  );
};

// Outstanding summary card
const OutstandingSummaryCard = ({ summary, closuresInfo, onCreateDeposit }) => {
  if (!summary) return null;
  
  const hasPending = summary.outstanding_balance > 0;
  const hasUnconfirmedClosures = closuresInfo?.unconfirmed_count > 0;
  
  return (
    <Card className={hasPending ? 'border-amber-200 bg-amber-50/30' : 'border-emerald-200 bg-emerald-50/30'}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="w-5 h-5" />
          Saldo por Depositar
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-slate-500 uppercase">Saldo Elegível</p>
            <p className={`text-2xl font-bold ${hasPending ? 'text-amber-600' : 'text-emerald-600'}`}>
              {formatCurrency(summary.outstanding_balance)}
            </p>
            <p className="text-xs text-slate-400">
              {summary.days_with_balance || summary.pending_closures_count || 0} dia{(summary.days_with_balance || summary.pending_closures_count || 0) !== 1 ? 's' : ''} com saldo
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Dias com Saldo</p>
            <p className="text-xl font-bold text-slate-700">
              {summary.days_with_balance || summary.pending_closures_count || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Mais Antigo</p>
            <p className="text-lg font-medium text-slate-700">
              {summary.oldest_pending_date 
                ? format(new Date(summary.oldest_pending_date), 'dd/MM/yyyy')
                : '—'}
            </p>
            {summary.days_since_oldest > 0 && (
              <p className={`text-xs ${summary.days_since_oldest > 7 ? 'text-red-500' : 'text-slate-400'}`}>
                há {summary.days_since_oldest} dias
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Total Depositável</p>
            <p className="text-lg font-medium text-slate-700">
              {formatCurrency(summary.total_depositable)}
            </p>
            <p className="text-xs text-slate-400">
              Alocado: {formatCurrency(summary.total_allocated)}
            </p>
          </div>
        </div>
        
        {/* Warning about unconfirmed closures */}
        {hasUnconfirmedClosures && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <strong>{closuresInfo.unconfirmed_count} fecho{closuresInfo.unconfirmed_count !== 1 ? 's' : ''} por confirmar</strong>
              <p className="text-blue-700 mt-0.5">
                Os fechos de caixa devem ser confirmados antes de poderem ser incluídos em depósitos. 
                O numerário de fechos não confirmados ({formatCurrency(closuresInfo.unconfirmed_cash_total)}) não está contabilizado acima.
              </p>
            </div>
          </div>
        )}
        
        {hasPending && (
          <div className="mt-4 pt-4 border-t border-amber-200">
            <Button onClick={onCreateDeposit} data-testid="create-deposit-btn">
              <Plus className="w-4 h-4 mr-2" />
              Registar Depósito
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Create deposit dialog
const CreateDepositDialog = ({ 
  open, 
  onOpenChange, 
  storeCode, 
  outstanding,
  onCreated 
}) => {
  const { getStoreName } = useStoreMapping();
  const [depositDate, setDepositDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [amount, setAmount] = useState(null);
  const [bankReference, setBankReference] = useState('');
  const [notes, setNotes] = useState('');
  const [fifoSuggestions, setFifoSuggestions] = useState([]);
  const [selectedAllocations, setSelectedAllocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Load FIFO suggestions when amount changes
  useEffect(() => {
    if (!amount || amount <= 0) {
      setFifoSuggestions([]);
      setSelectedAllocations({});
      return;
    }
    
    const loadSuggestions = async () => {
      try {
        const res = await axios.get(
          `${API}/retail-closures/outstanding/${storeCode}/fifo-suggestion?amount=${amount}&deposit_date=${depositDate}`
        );
        setFifoSuggestions(res.data || []);
        
        // Auto-select suggested allocations
        const auto = {};
        for (const s of res.data || []) {
          auto[s.closure_id] = roundMoney(s.suggested_amount);
        }
        setSelectedAllocations(auto);
      } catch (err) {
        console.error('Error loading suggestions:', err);
      }
    };
    
    loadSuggestions();
  }, [amount, storeCode, depositDate]);
  
  // Only closures up to the deposit date can be covered by this deposit
  const maxEligible = useMemo(() => roundMoney(Math.min(
    outstanding?.outstanding_balance || 0,
    (outstanding?.pending_closures || []).filter(c => c.date <= depositDate).reduce((t, c) => t + c.remaining, 0)
  )), [outstanding, depositDate]);

  const totalAllocated = useMemo(() => {
    return roundMoney(Object.values(selectedAllocations).reduce((sum, val) => sum + (val || 0), 0));
  }, [selectedAllocations]);
  
  const handleCreate = async () => {
    if (!amount || amount <= 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Create deposit with rounded amount
      const depositRes = await axios.post(`${API}/retail-closures/deposits`, {
        store_code: storeCode,
        deposit_date: depositDate,
        amount: roundMoney(amount),
        bank_reference: bankReference || null,
        notes: notes || null,
      });
      
      const depositId = depositRes.data.id;
      
      // Add allocations
      for (const [closureId, allocAmount] of Object.entries(selectedAllocations)) {
        if (allocAmount > 0) {
          await axios.post(`${API}/retail-closures/deposits/${depositId}/allocations`, {
            closure_id: closureId,
            allocated_amount: roundMoney(allocAmount),
          });
        }
      }
      
      // Confirm deposit
      await axios.post(`${API}/retail-closures/deposits/${depositId}/confirm`);
      
      onCreated();
      onOpenChange(false);
      
      // Reset form
      setAmount(null);
      setBankReference('');
      setNotes('');
      setSelectedAllocations({});
    } catch (err) {
      console.error('Error creating deposit:', err);
      setError(err.response?.data?.detail || 'Erro ao criar depósito');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Registar Depósito</DialogTitle>
          <DialogDescription>
            Registe um depósito bancário para {getStoreName(storeCode)}
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data do Depósito</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <Calendar className="w-4 h-4 mr-2" />
                    {format(new Date(depositDate), 'dd/MM/yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={new Date(depositDate)}
                    onSelect={(date) => date && setDepositDate(format(date, 'yyyy-MM-dd'))}
                    locale={pt}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div>
              <Label>Valor do Depósito</Label>
              <CurrencyInput
                value={amount}
                onChange={setAmount}
                allowNegative={false}
                className="text-lg font-medium"
                data-testid="deposit-amount-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Máx: {formatCurrency(maxEligible)} <span data-testid="deposit-max-hint">(fechos até à data do depósito)</span>
              </p>
            </div>
          </div>
          
          <div>
            <Label>Referência Bancária</Label>
            <Input
              placeholder="Nº movimento ou referência..."
              value={bankReference}
              onChange={(e) => setBankReference(e.target.value)}
            />
          </div>
          
          {fifoSuggestions.length > 0 && (
            <div>
              <Label className="text-sm">Alocação aos Fechos (FIFO)</Label>
              <div className="mt-2 border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                {fifoSuggestions.map((s) => (
                  <div key={s.closure_id} className="flex items-center justify-between p-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={(selectedAllocations[s.closure_id] || 0) > 0}
                        onCheckedChange={(checked) => {
                          setSelectedAllocations(prev => ({
                            ...prev,
                            [s.closure_id]: checked ? roundMoney(s.suggested_amount) : 0
                          }));
                        }}
                      />
                      <span>{format(new Date(s.business_date), 'dd/MM/yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Disp: {formatCurrency(s.available)}</span>
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      <CurrencyInput
                        value={selectedAllocations[s.closure_id] || null}
                        onChange={(val) => {
                          setSelectedAllocations(prev => ({
                            ...prev,
                            [s.closure_id]: val || 0
                          }));
                        }}
                        allowNegative={false}
                        className="w-24 h-7 text-sm"
                        data-testid={`alloc-input-${s.closure_id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Total alocado: {formatCurrency(totalAllocated)} de {formatCurrency(amount || 0)}
              </p>
            </div>
          )}
          
          {/* Validation messages */}
          {amount !== null && amount > 0 && amount > maxEligible + 0.001 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                O valor do depósito ({formatCurrency(amount)}) não pode ser superior ao saldo elegível até {format(new Date(depositDate), 'dd/MM/yyyy')} de {formatCurrency(maxEligible)}.
              </AlertDescription>
            </Alert>
          )}
          
          {amount !== null && amount > 0 && totalAllocated > 0 && Math.abs(totalAllocated - amount) > 0.01 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                O total alocado ({formatCurrency(totalAllocated)}) deve ser igual ao valor do depósito ({formatCurrency(amount)}).
              </AlertDescription>
            </Alert>
          )}
          
          <div>
            <Label>Observações</Label>
            <Textarea
              placeholder="Notas adicionais..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleCreate} 
            disabled={
              loading || 
              !amount || 
              amount <= 0 || 
              totalAllocated === 0 ||
              amount > maxEligible + 0.001 ||
              Math.abs(totalAllocated - amount) > 0.01
            }
            data-testid="confirm-deposit-btn"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Confirmar Depósito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main component
export default function RetailDepositos() {
  const { user } = useContext(AuthContext);
  
  // Use shared filter hook
  const {
    selectedStore,
    setSelectedStore,
    period,
    setPeriod,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    startDate,
    endDate,
    filterProps,
  } = useStorePeriodFilter({ defaultPeriod: 'this_month', defaultStore: '' });
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Data
  const [outstanding, setOutstanding] = useState(null);
  const [closuresInfo, setClosuresInfo] = useState(null);
  const [deposits, setDeposits] = useState([]);
  
  // Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Auto-select store if user has only one
  useEffect(() => {
    if (!selectedStore && user?.allowed_warehouses?.length === 1) {
      setSelectedStore(user.allowed_warehouses[0]);
    }
  }, [user, selectedStore, setSelectedStore]);
  
  // Load data
  const loadData = async () => {
    if (!selectedStore) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.set('store_code', selectedStore);
      params.set('start_date', startDate);
      params.set('end_date', endDate);
      params.set('limit', '50');
      
      const [outstandingRes, depositsRes, closuresRes] = await Promise.all([
        axios.get(`${API}/retail-closures/outstanding/${selectedStore}`),
        axios.get(`${API}/retail-closures/deposits?${params.toString()}`),
        axios.get(`${API}/retail-closures/closures-range?store_code=${selectedStore}&start_date=${startDate}&end_date=${endDate}`),
      ]);
      
      setOutstanding(outstandingRes.data);
      setDeposits(depositsRes.data || []);
      
      // Calculate unconfirmed closures info
      const closures = closuresRes.data || [];
      const unconfirmed = closures.filter(c => 
        ['OPEN', 'READY_TO_CLOSE', 'REOPENED'].includes(c.status)
      );
      const unconfirmedCashTotal = unconfirmed.reduce((sum, c) => 
        sum + (c.expected?.cash || 0), 0
      );
      
      setClosuresInfo({
        unconfirmed_count: unconfirmed.length,
        unconfirmed_cash_total: roundMoney(unconfirmedCashTotal),
      });
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.detail || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
  }, [selectedStore, startDate, endDate]);
  
  const handleRefresh = () => {
    loadData();
  };

  const canCancel = ['admin', 'procurement_manager'].includes(user?.role);
  const handleCancelDeposit = async (deposit) => {
    const reason = window.prompt(`Anular o depósito de ${formatCurrency(deposit.amount)}? Indique o motivo:`);
    if (!reason?.trim()) return;
    try {
      await axios.post(`${API}/retail-closures/deposits/${deposit.id}/cancel`, { reason: reason.trim() });
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao anular depósito');
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="depositos-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Banknote className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Depósitos Bancários
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Controlo de depósitos de numerário
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* Shared Store/Period Filter */}
              <StorePeriodFilter
                {...filterProps}
                onRefresh={handleRefresh}
                loading={loading}
                showAllStores={false}
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      )}
      
      {/* No store selected */}
      {!loading && !selectedStore && (
        <Card>
          <CardContent className="py-12 text-center">
            <Store className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">Selecione uma loja</h3>
          </CardContent>
        </Card>
      )}
      
      {/* Content */}
      {!loading && selectedStore && (
        <div className="space-y-6">
          {/* Outstanding summary */}
          <OutstandingSummaryCard 
            summary={outstanding}
            closuresInfo={closuresInfo}
            onCreateDeposit={() => setShowCreateDialog(true)}
          />
          
          {/* Deposits list */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Histórico de Depósitos</CardTitle>
            </CardHeader>
            <CardContent>
              {deposits.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  Nenhum depósito registado
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Referência</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Criado por</TableHead>
                      {canCancel && <TableHead />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deposits.map((deposit) => (
                      <TableRow key={deposit.id}>
                        <TableCell>
                          {format(new Date(deposit.deposit_date), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(deposit.amount)}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {deposit.bank_reference || '—'}
                        </TableCell>
                        <TableCell>
                          <DepositStatusBadge status={deposit.status} />
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {deposit.created_by_name || deposit.created_by || '—'}
                        </TableCell>
                        {canCancel && (
                          <TableCell className="text-right">
                            {deposit.status !== 'CANCELLED' && (
                              <Button size="sm" variant="ghost" className="text-red-600" data-testid={`cancel-deposit-${deposit.id}`}
                                onClick={() => handleCancelDeposit(deposit)}>Anular</Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Create deposit dialog */}
      <CreateDepositDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        storeCode={selectedStore}
        outstanding={outstanding}
        onCreated={loadData}
      />
      </div>
    </div>
  );
}
